import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IFirm } from 'app/models/firms/firm';
import { IFirmCredential } from 'app/models/firms/firm-credential';
import { FirmService } from 'app/services/firms/firm.service';
import { ToastrService } from 'ngx-toastr';
import { PUBLIC_FIRM_CREDENTIALS, PUBLIC_FIRM_CREDENTIAL_MAPPING } from './firm-edit.constants';

@Component({
  selector: 'app-firm-edit-credential-dialog',
  templateUrl: './firm-edit-credential-dialog.component.html',
  styleUrls: ['./firm-edit-credential-dialog.component.scss']
})
export class FirmEditCredentialDialogComponent implements OnInit {
  public firmCredentials: string[] = Object.values(PUBLIC_FIRM_CREDENTIALS);
  public selectedCredential = '';
  public credentialToAdd: IFirmCredential = { id: 0, firmLeadCredentialType: '' };
  public uiData = LEAD_BASED_PAINT;
  public credentialMapping = PUBLIC_FIRM_CREDENTIAL_MAPPING;
  public firmValidatedCredential: { isValid: boolean; staffIds: any[]; totalStaff: number; expirationDate: string } = {
    isValid: false,
    staffIds: [],
    totalStaff: 0,
    expirationDate: ''
  };

  constructor(
    private readonly dialogRef: MatDialogRef<FirmEditCredentialDialogComponent>,
    public firmService: FirmService,
    @Inject(MAT_DIALOG_DATA) public data: { firm: IFirm; credential: IFirmCredential | null; firmCredentials: IFirmCredential[]; staffCerts: any[] },
    private readonly toastr: ToastrService,
  ) {
    if (this.data.credential !== null) {
      this.credentialToAdd = { ...this.data.credential };
      this.selectedCredential = this.data.credential.firmLeadCredentialType;
      this.validateFirmCredentials();
    }
  }

  ngOnInit(): void {
    this.firmCredentials = [this.data?.credential?.firmLeadCredentialType]
      .concat(Object.values(PUBLIC_FIRM_CREDENTIALS).filter((credential) =>
        !!credential && !this.data.firmCredentials.some((currentCredential) => currentCredential.firmLeadCredentialType === credential)
      ))
      .filter((credential, index, values) => !!credential && values.indexOf(credential) === index);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    this.credentialToAdd.firmLeadCredentialType = this.selectedCredential;
    this.firmService.saveFirmCredential(this.data.firm.id, this.credentialToAdd).subscribe({
      next: () => {
        this.toastr.success('Successfully saved firm credential');
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.toastr.error('An error occurred trying to save the firm credential: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  onCredentialChange(): void {
    this.firmValidatedCredential = { isValid: false, staffIds: [], totalStaff: 0, expirationDate: '' };
    this.validateFirmCredentials();
  }

  validateFirmCredentials(): void {
    const value = this.firmCredentialsMappedToPersonCerts();

    this.firmValidatedCredential = {
      isValid: value.isValid,
      staffIds: value.staffIds,
      totalStaff: value.staffIds.length,
      expirationDate: value.expirationDate
    };
  }

  firmCredentialsMappedToPersonCerts(): { isValid: boolean; staffIds: any[]; expirationDate: string } {
    const returnObject = { isValid: false, staffIds: [], expirationDate: '' };
    const credentialTypes = this.credentialMapping[this.selectedCredential] ?? [];
    const now = new Date();
    const latestByPerson = new Map<any, any>();
    let furthestExpirationTime = 0;
    let furthestExpirationIso = '';

    this.data.staffCerts?.forEach((member) => {
      (member.certs || []).forEach((cert: any) => {
        if (!credentialTypes.includes(cert.codeType)) {
          return;
        }

        const expiration = cert.expirationDate ? new Date(cert.expirationDate) : null;
        if (!expiration || isNaN(expiration.getTime()) || expiration < now) {
          return;
        }

        returnObject.isValid = true;

        const personId = cert.person?.id;
        const staffObject = {
          id: personId,
          display: cert.person?.id + ': ' + cert.person?.firstName + ' ' + cert.person?.lastName,
          expiration: cert.expirationDate,
          codeType: cert.codeType
        };

        if (!latestByPerson.has(personId)) {
          latestByPerson.set(personId, staffObject);
        } else {
          const existing = latestByPerson.get(personId);
          const existingDate = existing?.expiration ? new Date(existing.expiration) : null;
          if (!existingDate || expiration > existingDate) {
            latestByPerson.set(personId, staffObject);
          }
        }

        const expirationTime = expiration.getTime();
        if (expirationTime > furthestExpirationTime) {
          furthestExpirationTime = expirationTime;
          furthestExpirationIso = expiration.toISOString();
        }
      });
    });

    returnObject.staffIds = Array.from(latestByPerson.values())
      .sort((left: any, right: any) => new Date(right.expiration).getTime() - new Date(left.expiration).getTime());
    returnObject.expirationDate = furthestExpirationIso;

    return returnObject;
  }
}
